import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, UserPlus, Eye, EyeOff, Gamepad2, AlertCircle, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function SignupPage() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const passwordRequirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains a number', met: /\d/.test(password) },
    { label: 'Contains uppercase', met: /[A-Z]/.test(password) }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    const result = await signup(username, email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const [particles] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 15,
      duration: 10 + Math.random() * 10,
      color: ['#00f5ff', '#ff00ff', '#00ff88', '#ffff00'][Math.floor(Math.random() * 4)]
    }))
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(100vh) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(100px); opacity: 0; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes gridMove {
          0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
          100% { transform: perspective(500px) rotateX(60deg) translateY(50px); }
        }
        
        @keyframes scanLine {
          0% { top: -10%; }
          100% { top: 110%; }
        }
        
        @keyframes logoGlow {
          0% { filter: drop-shadow(0 0 10px rgba(0, 245, 255, 0.5)); }
          100% { filter: drop-shadow(0 0 20px rgba(255, 0, 255, 0.5)); }
        }
        
        @keyframes borderPulse {
          0%, 100% { border-color: rgba(255, 0, 255, 0.3); }
          50% { border-color: rgba(0, 245, 255, 0.5); }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Rajdhani', sans-serif; }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #fff;
          -webkit-box-shadow: 0 0 0px 1000px rgba(0, 245, 255, 0.1) inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {/* Animated Grid Background */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        {/* Particles */}
        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: 'fixed',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              left: `${p.left}%`,
              animationName: 'float',
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              background: p.color,
              boxShadow: `0 0 10px ${p.color}, 0 0 20px ${p.color}`,
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        ))}

        {/* Scan Line */}
        <div style={{
          position: 'fixed',
          height: '2px',
          width: '100%',
          left: 0,
          background: 'linear-gradient(90deg, transparent, #ff00ff, transparent)',
          animation: 'scanLine 8s linear infinite',
          pointerEvents: 'none',
          zIndex: 1
        }} />

        {/* Signup Card */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '450px',
          background: 'rgba(10, 10, 15, 0.9)',
          border: '1px solid rgba(255, 0, 255, 0.2)',
          borderRadius: '20px',
          padding: '40px',
          backdropFilter: 'blur(20px)',
          animation: 'borderPulse 3s ease-in-out infinite'
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              <Gamepad2 
                size={48} 
                style={{ 
                  color: '#ff00ff',
                  marginBottom: '15px',
                  animation: 'logoGlow 2s ease-in-out infinite alternate'
                }} 
              />
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '1.8rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #ff00ff, #00f5ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '4px'
              }}>
                CUGS//GAMES
              </div>
            </div>
          </Link>

          <h1 style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: '1.5rem',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '25px',
            color: '#fff'
          }}>
            JOIN THE GAME
          </h1>

          {/* Error Message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              background: 'rgba(255, 50, 50, 0.1)',
              border: '1px solid rgba(255, 50, 50, 0.3)',
              borderRadius: '10px',
              marginBottom: '20px',
              animation: 'shake 0.3s ease-in-out'
            }}>
              <AlertCircle size={20} color="#ff3232" />
              <span style={{ color: '#ff3232', fontSize: '0.95rem' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username Input */}
            <div style={{ marginBottom: '18px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Username
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <User 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '15px', 
                    color: '#00ff88',
                    zIndex: 1
                  }} 
                />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose your gamer tag"
                  required
                  minLength={3}
                  maxLength={20}
                  style={{
                    width: '100%',
                    padding: '14px 15px 14px 50px',
                    background: 'rgba(0, 255, 136, 0.05)',
                    border: '1px solid rgba(0, 255, 136, 0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00ff88';
                    e.target.style.boxShadow = '0 0 20px rgba(0, 255, 136, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 255, 136, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Email Input */}
            <div style={{ marginBottom: '18px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Email
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Mail 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '15px', 
                    color: '#00f5ff',
                    zIndex: 1
                  }} 
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 15px 14px 50px',
                    background: 'rgba(0, 245, 255, 0.05)',
                    border: '1px solid rgba(0, 245, 255, 0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00f5ff';
                    e.target.style.boxShadow = '0 0 20px rgba(0, 245, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '18px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Password
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Lock 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '15px', 
                    color: '#ff00ff',
                    zIndex: 1
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a strong password"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 50px 14px 50px',
                    background: 'rgba(255, 0, 255, 0.05)',
                    border: '1px solid rgba(255, 0, 255, 0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff00ff';
                    e.target.style.boxShadow = '0 0 20px rgba(255, 0, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 0, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.5)',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Password Requirements */}
              {password && (
                <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {passwordRequirements.map((req, i) => (
                    <div 
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '0.75rem',
                        color: req.met ? '#00ff88' : 'rgba(255, 255, 255, 0.4)'
                      }}
                    >
                      <Check size={12} />
                      {req.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Confirm Password
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Lock 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '15px', 
                    color: password && confirmPassword && password === confirmPassword ? '#00ff88' : '#ffff00',
                    zIndex: 1
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 15px 14px 50px',
                    background: 'rgba(255, 255, 0, 0.05)',
                    border: `1px solid ${password && confirmPassword && password === confirmPassword ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 0, 0.2)'}`,
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ffff00';
                    e.target.style.boxShadow = '0 0 20px rgba(255, 255, 0, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = password && confirmPassword && password === confirmPassword ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 255, 0, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Signup Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '15px',
                background: isLoading 
                  ? 'rgba(255, 0, 255, 0.3)' 
                  : 'linear-gradient(135deg, #ff00ff, #00f5ff)',
                border: 'none',
                borderRadius: '10px',
                color: '#000',
                fontSize: '1.1rem',
                fontWeight: '700',
                fontFamily: "'Orbitron', monospace",
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}
              onMouseOver={(e) => {
                if (!isLoading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 30px rgba(255, 0, 255, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {isLoading ? (
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid rgba(0, 0, 0, 0.3)',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <>
                  <UserPlus size={22} />
                  CREATE ACCOUNT
                </>
              )}
            </button>
          </form>

          {/* Login Link */}
          <p style={{
            textAlign: 'center',
            marginTop: '25px',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '1rem'
          }}>
            Already have an account?{' '}
            <Link 
              to="/login" 
              style={{
                color: '#ff00ff',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.textShadow = '0 0 10px #ff00ff'}
              onMouseOut={(e) => e.target.style.textShadow = 'none'}
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}